/**
 * RFQ Create Wizard — Step 5 (Supplier Selection) and Step 6 (Review).
 *
 * Split out of RFQWizardSteps.tsx to keep both files under the size limit.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Building, Search, Loader2 } from 'lucide-react';
import type {
  BasicDetailsForm,
  LineItemForm,
  AdditionalCostForm,
  RequiredCertificateForm,
  SupplierOption,
} from './rfqWizardTypes';
import type { RFQTypeOption } from './RFQWizardSteps';
import { useRfqMoney } from './useRfqMoney';

/* ------------------------------------------------------------------ */
/*  Step 5: Supplier Selection                                         */
/* ------------------------------------------------------------------ */

interface Step5Props {
  suppliers: SupplierOption[];
  selectedSuppliers: number[];
  onToggle: (companyId: number) => void;
  /** True while the supplier list is still loading — shows a spinner
   *  instead of the misleading "No suppliers available". */
  loading?: boolean;
}

export function SupplierSelectionStep({ suppliers, selectedSuppliers, onToggle, loading = false }: Step5Props) {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) => s.company_name.toLowerCase().includes(q));
  }, [suppliers, query]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Select Suppliers</h3>
        <p className="text-sm text-muted-foreground">
          Choose which suppliers should receive this RFQ. Leave empty to send later.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search suppliers by name..."
          className="pl-9"
          aria-label="Search suppliers"
          disabled={loading}
        />
      </div>

      <div className="border rounded-lg max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading suppliers...
          </div>
        ) : suppliers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No suppliers available
          </div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No suppliers match &quot;{query.trim()}&quot;
          </div>
        ) : (
          visible.map((supplier) => (
            <div
              key={supplier.id}
              className={`flex items-center p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 ${
                selectedSuppliers.includes(supplier.company) ? 'bg-primary/5' : ''
              }`}
              onClick={() => onToggle(supplier.company)}
            >
              <Checkbox
                checked={selectedSuppliers.includes(supplier.company)}
                onCheckedChange={() => onToggle(supplier.company)}
                onClick={(e) => e.stopPropagation()}
              />
              <Building className="h-4 w-4 ml-3 mr-2 text-muted-foreground" />
              <span className="font-medium">{supplier.company_name}</span>
            </div>
          ))
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {selectedSuppliers.length} supplier(s) selected
        {!loading && suppliers.length > 0 && (
          <span> · showing {visible.length} of {suppliers.length}</span>
        )}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 6: Review                                                     */
/* ------------------------------------------------------------------ */

interface Step6Props {
  basicDetails: BasicDetailsForm;
  lineItems: LineItemForm[];
  additionalCosts: AdditionalCostForm[];
  requiredCertificates: RequiredCertificateForm[];
  selectedSuppliers: number[];
  suppliers: SupplierOption[];
  totalValue: number;
  // CR-5466: needed to resolve the RFQ Type id back to its display name
  rfqTypes?: RFQTypeOption[];
  /** Currency the RFQ will be raised in (the tenant's reporting currency). */
  currency: string;
  /** NDA file picked on the Certificates & NDA step; shown when NDA is required. */
  ndaFile?: File | null;
}

// CR-5466: Review step rebuilt to reflect the redesigned Basic Details.
// Reference / RFQ Type / RFQ Date / Deadline / NDA replace the old
// category/priority/currency/contract_type display row. Money is shown in
// the RFQ currency via the shared formatter.

export function ReviewStep({
  basicDetails,
  lineItems,
  additionalCosts,
  requiredCertificates,
  selectedSuppliers,
  suppliers,
  totalValue,
  rfqTypes = [],
  currency,
  ndaFile = null,
}: Step6Props) {
  const formatMoney = useRfqMoney(currency);
  const rfqTypeName = basicDetails.rfq_type
    ? rfqTypes.find((t) => t.id === basicDetails.rfq_type)?.name ?? '—'
    : '—';
  return (
    <div className="space-y-6">
      <h3 className="font-semibold">Review RFQ</h3>

      <Card>
        <CardHeader>
          <CardTitle>{basicDetails.reference || 'New RFQ'}</CardTitle>
          <CardDescription>{basicDetails.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Reference:</span>
              <span className="ml-2 font-medium">{basicDetails.reference || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">RFQ Type:</span>
              <span className="ml-2 font-medium">{rfqTypeName}</span>
            </div>
            <div>
              <span className="text-muted-foreground">RFQ Date:</span>
              <span className="ml-2 font-medium">{basicDetails.issue_date || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Deadline:</span>
              <span className="ml-2 font-medium">{basicDetails.response_deadline}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Required Date:</span>
              <span className="ml-2 font-medium">{basicDetails.required_delivery_date || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">NDA:</span>
              <span className="ml-2 font-medium">
                {basicDetails.nda_required
                  ? (ndaFile ? `Required — ${ndaFile.name}` : 'Required — no document uploaded')
                  : 'Not Required'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Currency:</span>
              <span className="ml-2 font-medium">{currency}</span>
            </div>
            <div className="col-span-3">
              <span className="text-muted-foreground">Delivery Location:</span>
              <span className="ml-2 font-medium">{basicDetails.delivery_location?.trim() || '—'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Line Items ({lineItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {lineItems.map((item, index) => (
              <div key={index} className="flex justify-between text-sm py-1 border-b last:border-b-0">
                <span>{item.item_description || `Item ${index + 1}`}</span>
                <span className="font-medium">
                  {item.quantity} x {formatMoney(item.target_unit_price ?? 0)}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {additionalCosts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Additional Costs ({additionalCosts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {additionalCosts.map((cost, index) => (
                <div key={index} className="flex justify-between text-sm py-1 border-b last:border-b-0">
                  <span className="capitalize">{cost.cost_type}: {cost.description}</span>
                  <span className="font-medium">{formatMoney(cost.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {requiredCertificates.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Required Certificates ({requiredCertificates.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {requiredCertificates.map((cert, index) => (
                <Badge key={index} variant={cert.is_mandatory ? 'default' : 'secondary'}>
                  {cert.certificate_name || cert.certificate_type.replace('_', ' ').toUpperCase()}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Selected Suppliers ({selectedSuppliers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedSuppliers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No suppliers selected. RFQ will be saved as draft.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedSuppliers.map(companyId => {
                const supplier = suppliers.find(s => s.company === companyId);
                return (
                  <Badge key={companyId} variant="outline">
                    {supplier?.company_name || `Company ${companyId}`}
                  </Badge>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-muted/50">
        <CardContent className="pt-4">
          <div className="flex justify-between items-center text-lg font-semibold">
            <span>Estimated Total Value:</span>
            <span>{formatMoney(totalValue)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
